import { CopywritingCampaign } from "../../List/pages/ListEditCopywriting/ListEditCopywriting";
import { ListPerson } from "../../List/schema/list";

/**
 * Resolves the campaign config that the Ready-to-Send Campaign tab should display for a prospect.
 *
 * Resolution order:
 * 1. Prospect-level custom sequence (`prospect.campaignConfig`) → `isCustom: true`.
 * 2. The list campaign the prospect is assigned to (`prospect.campaignId`).
 * 3. A list campaign whose `languages` match the prospect's language.
 * 4. The list's default campaign (`isDefault === true`).
 * 5. Otherwise `undefined` — the lead has no campaign to show, so the caller renders the
 *    "No outreach yet" signpost instead of the editor.
 *
 * Unlike {@link getListCampaignConfig}, this deliberately does NOT fall back to an arbitrary
 * non-default campaign: a list with only non-default campaigns (and no language match) resolves
 * to `undefined`.
 */
export const resolveSelectedCampaignConfig = (
	prospect: ListPerson
): (CopywritingCampaign & { isCustom: boolean }) | undefined => {
	if (prospect.campaignConfig) {
		return { ...(prospect.campaignConfig as CopywritingCampaign), isCustom: true };
	}

	const allCampaigns = prospect.list?.campaignConfig as
		| { [key: string]: CopywritingCampaign }
		| undefined;

	if (!allCampaigns) {
		return undefined;
	}

	const allValidCampaignIds = Object.keys(allCampaigns).filter(
		(campaign) => allCampaigns[campaign]?.isValid
	);

	if (allValidCampaignIds.length === 0) {
		return undefined;
	}

	if (prospect.campaignId && allCampaigns[prospect.campaignId]) {
		return { ...(allCampaigns[prospect.campaignId] as CopywritingCampaign), isCustom: false };
	}

	const languages = prospect.prospect?.language
		?.split(",")
		?.map((lang: string) => lang.trim().toLowerCase());

	const languageBasedCampaignId = allValidCampaignIds.find((campaign) =>
		allCampaigns[campaign]?.languages?.some((lang: string) =>
			languages?.includes(lang.toLowerCase())
		)
	);

	const defaultCampaignId = allValidCampaignIds.find(
		(campaign) => allCampaigns[campaign].isDefault
	);

	const resolvedCampaignId = languageBasedCampaignId || defaultCampaignId;

	if (!resolvedCampaignId) {
		return undefined;
	}

	return {
		...(allCampaigns[resolvedCampaignId] || {}),
		id: resolvedCampaignId,
		isCustom: false,
	} as CopywritingCampaign & { isCustom: boolean };
};
